import { Flex, Stack } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useSuspenseQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { Proceeding } from '@/module/Author/Proceeding';
import { ProceedingCard } from '@/module/Author/Proceeding/components/Card/ProceedingCard';
import { CriteriaProceedingSelectAddModal } from '@/module/Laboratory/criteria/components/CriteriaProceedingSelectAddModal';
import { getCenterReportCriteriaDataOptions } from '@/module/ResearchCenter';

import { NoContentPlaceholder, PendingSpinner } from '@/core/components';

import { Id } from '@feature/utils';

import { CriteriaHeaderButtons } from './components/components/CriteriaHeaderButtons';
import { CriteriaItemDeleteButton } from './components/components/CriteriaItemDeleteButton';
import { useSaveCriteriaData } from './components/hooks/useSaveCriteriaData';

export interface CenterReportProceedingSelectProps {
  labId: Id;
  criteriaCode: string;
}

export function CenterReportProceedingSelect({ labId, criteriaCode }: CenterReportProceedingSelectProps) {
  const [opened, { open, close }] = useDisclosure(false);
  const { t } = useTranslation();
  const {
    data: {
      completed,
      proceedings: { available, selected },
    },
  } = useSuspenseQuery(getCenterReportCriteriaDataOptions(labId, criteriaCode));
  const { onSubmit, isPending } = useSaveCriteriaData(labId, criteriaCode, close);

  const onAdd = (proceeding: Proceeding) => onSubmit({ toAdd: proceeding });
  const onRemove = (proceeding: Proceeding) => onSubmit({ toRemove: proceeding });

  return (
    <div>
      <CriteriaHeaderButtons
        completed={completed}
        criteriaCode={criteriaCode}
        labId={labId}
        onAdd={open}
      />
      {opened && (
        <CriteriaProceedingSelectAddModal
          available={available}
          selected={selected}
          close={close}
          onAdd={onAdd}
        />
      )}
      <>{isPending && <PendingSpinner />}</>
      {!isPending && (
        <>
          {selected.length > 0 && (
            <Stack>
              {selected.map((proceeding, index) => (
                <Flex
                  align="center"
                  key={proceeding.id}
                >
                  {!completed && (
                    <CriteriaItemDeleteButton
                      index={index}
                      onRemove={() => onRemove(proceeding)}
                    />
                  )}

                  <ProceedingCard
                    proceeding={proceeding}
                    onClick={() => window.open(`/author/proceeding/${proceeding.id}`)}
                  />
                </Flex>
              ))}
            </Stack>
          )}
          {selected.length === 0 && <NoContentPlaceholder title={t('centerReport.noCriteriaContent')} />}
        </>
      )}
    </div>
  );
}
